import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Button,
  useTheme,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import useApiEp from "../../hooks/useApiEp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";

// Color palette for different statuses
const statusColors = {
  queued: "#FFB74D",
  pending: "#64B5F6",
  sent: "#4FC3F7",
  delivered: "#81C784",
  read: "#4DB6AC",
  failed: "#E57373",
  accepted: "#BA68C8",
  cancelled: "#90A4AE",
};

const StatusStatsLive = ({ id, type }) => {
  const theme = useTheme();
  const {
    data: statsD,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(
    `/campaign/get-report-status-analytics/${id}?type=${type ? type : ""}`
  );
  const [retrying, setRetrying] = useState(false);

  const handleRetryQueued = useCallback(async () => {
    try {
      setRetrying(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/retry-queued/${id}`,
        {},
        getHeader()
      );
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setRetrying(false);
    }
  }, [id]);
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <ErrorOutlineIcon color="error" fontSize="large" />
        <Typography color="error" variant="h6" mt={2}>
          Failed to load analytics
        </Typography>
        <Typography color="textSecondary" variant="body2" mt={1}>
          {error?.message || "Unknown error occurred"}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={refetch}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!statsD) return null;

  // Prepare data for charts
  const chartData = Object.entries(statsD).map(([status, count]) => ({
    status,
    count,
    color: statusColors[status] || "#9E9E9E",
  }));

  // Sort by count descending
  chartData.sort((a, b) => b.count - a.count);

  // Total messages calculation
  const totalMessages = Object.values(statsD).reduce(
    (sum, count) => sum + count,
    0
  );

  // Function to handle retry for queued messages

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {chartData.map(({ status, count, color }) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={status}>
                <Card
                  sx={{
                    borderLeft: `4px solid ${color}`,
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      {status.toUpperCase()}
                    </Typography>
                    <Typography variant="h5">
                      {count.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {totalMessages > 0
                        ? `${((count / totalMessages) * 100).toFixed(1)}%`
                        : "0%"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Message Status Distribution
              </Typography>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [value.toLocaleString(), "Count"]}
                    />
                    <Legend />
                    <Bar dataKey="count" name="Message Count">
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Status Percentage</Typography>
                {/* {statsD.queued > 0 && (
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={retrying}
                    onClick={handleRetryQueued}
                    startIcon={<RefreshIcon />}
                  >
                    Retry Queued
                  </LoadingButton>
                )} */}
              </Box>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="status"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value.toLocaleString()} (${(
                          props.payload.percent * 100
                        ).toFixed(1)}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Legend */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Status Legend
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {Object.entries(statusColors).map(([status, color]) => (
                  <Chip
                    key={status}
                    label={status.toUpperCase()}
                    sx={{
                      backgroundColor: color,
                      color: theme.palette.getContrastText(color),
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatusStatsLive;
